import { Entity } from '@core/api/entity';
import { Model, Serializer } from '@junte/serialize-ts';

import { FileObject, FileObjectSerializer } from './file-object.model';
import { PartnerDTO } from '@core/dto/partner.dto';
export interface IssuerConfig {
  uid: string;
  userType: string;
  accessType: string;
}

export interface WealthManagerConfig {
  uid: string;
  userType: string;
  accessType: string;
}

export interface ClientConfig {
  uid: string;
  userType: string;
  clientClass: {
    codename: string;
    name: string;
  };
  accessType: string;
  requiresAdvisor: boolean;
}

export interface SelfRegistrationConfig {
  issuerConfig: IssuerConfig;
  wealthManagerConfig: WealthManagerConfig;
  clientConfigs: ClientConfig[];
  blacklistedDomains: string;
}

export interface Language {
  id: number;
  uid: string;
  code: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
  nativeName: string;
  flagEmoji: string;
  rtl: boolean;
  createdAt: string;
  modifiedAt: string;
  fallbackText?: string;
}

export interface documentCategory {
  partner_uid: string;
}

/** API may send a comma-separated string or a string array (e.g. alertUsers, allowedRegistrationClientClasses). */
function normalizeStringListField(value: unknown): string[] {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter((s) => s.length > 0);
  }
  if (typeof value === 'string') {
    return value.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return [];
}

export class PartnerSerializer implements Serializer<Partner> {
  public deserialize(dto: PartnerDTO): Partner {
    const fos = new FileObjectSerializer();
    const anyDto = dto as any;

    return Partner.create({
      uid: dto.uid,
      id: dto.id,
      fullCompanyName: dto.fullCompanyName,
      logoImageLight: (dto.logoImageLight || anyDto.logo_image_light)
        ? fos.deserialize(dto.logoImageLight || anyDto.logo_image_light)
        : null,
      logoImageDark: (dto.logoImageDark || anyDto.logo_image_dark)
        ? fos.deserialize(dto.logoImageDark || anyDto.logo_image_dark)
        : null,
      faviconImage: (dto.faviconImage || anyDto.favicon_image)
        ? fos.deserialize(dto.faviconImage || anyDto.favicon_image)
        : null,
      loginPageImage: (dto.loginPageImage || anyDto.login_page_image)
        ? fos.deserialize(dto.loginPageImage || anyDto.login_page_image)
        : null,
      loginPageBackgroundColor: dto.loginPageBackgroundColor || anyDto.login_page_background_color,

      financialUmbrella: dto.financialUmbrella,
      commissionBasisText: dto.commissionBasisText,
      proportionCommissionUpfront: dto.proportionCommissionUpfront,
      minCommissionUpfrontBasisPoints: dto.minCommissionUpfrontBasisPoints,
      proportionManagementFee: dto.proportionManagementFee,
      minManagementFeeBasisPoints: dto.minManagementFeeBasisPoints,
      proportionCarriedInterest: dto.proportionCarriedInterest,
      minCarriedInterestBasisPoints: dto.minCarriedInterestBasisPoints,

      domain: dto.domain,
      website: dto.website,
      isMarketplace: dto.isMarketplace ?? anyDto.is_marketplace ?? false,
      isDueDiligence: dto.isDueDiligence ?? anyDto.is_due_diligence ?? false,

      allowIssuerSelfRegister: dto.allowIssuerSelfRegister,
      allowWmSelfRegister: dto.allowWmSelfRegister,
      allowedRegistrationClientClasses: normalizeStringListField(dto.allowedRegistrationClientClasses),
      allowClientSelfRegister: dto.allowClientSelfRegister,
      hasOrganizations: dto.hasOrganizations ?? false,
      clientManagementCompany: dto.clientManagementCompany,
      selfRegisterClientType: dto.selfRegisterClientType,

      primaryColor: dto.primaryColor,
      secondaryColor: dto.secondaryColor,
      buttonRounding: dto.buttonRounding,
      sidebarBackgroundColor: dto.sidebarBackgroundColor,
      sidebarTextColor: dto.sidebarTextColor,
      theme: dto.theme,
      complianceTextEn: dto.complianceTextEn,
      issuerConsentTextAi: dto.issuerConsentTextAi,
      issuerConsentDataSharingText: dto.issuerConsentDataSharingText,
      complianceTextDe: dto.complianceTextDe,

      impressumUrl: dto.impressumUrl,
      termsAndConditionsUrl: dto.termsAndConditionsUrl,
      contactUrl: dto.contactUrl,
      privacyPolicyUrl: dto.privacyPolicyUrl,
      alertUsers: normalizeStringListField(dto.alertUsers),
      availableLanguages: dto.availableLanguages,
      versionNumber: dto.versionNumber,
      registrationConfig: dto.registrationConfig
        ? {
          issuerConfig: dto.registrationConfig.issuerConfig,
          wealthManagerConfig: dto.registrationConfig.wealthManagerConfig,
          clientConfigs: dto.registrationConfig.clientConfigs,
          blacklistedDomains: dto.registrationConfig.blacklistedDomains,
        }
        : null,
      selectedRegions: normalizeStringListField(dto.selectedRegions),
      includedCountries: normalizeStringListField(dto.includedCountries),
      excludedCountries: normalizeStringListField(dto.excludedCountries),
      effectiveAllowedCountries: normalizeStringListField(dto.effectiveAllowedCountries),
    });
  }

  public serialize(model: Partner): PartnerDTO {
    const dto = Object.assign({}, model) as any as PartnerDTO;
    return dto;
  }
}

@Model(new PartnerSerializer())
export class Partner extends Entity {
  public id: string;
  public uid: string;
  public fullCompanyName: string;
  public logoImageLight: FileObject;
  public logoImageDark: FileObject;
  public faviconImage: FileObject;
  public loginPageImage: FileObject;


  public domain: string;
  public website: string;
  public isMarketplace: boolean;
  public isDueDiligence: boolean;
  public allowIssuerSelfRegister: boolean;
  public allowWmSelfRegister: boolean;
  public allowClientSelfRegister: boolean;
  public hasOrganizations: boolean;
  public allowedRegistrationClientClasses: string[];
  public clientManagementCompany: {
    fullCompanyName: string;
    uid: string;
  };
  public selfRegisterClientType: string;
  public primaryColor: string;
  public secondaryColor: string;
  public sidebarBackgroundColor: string;
  public sidebarTextColor: string;
  public loginPageBackgroundColor: string;
  public buttonRounding: string;
  public theme: string;
  public complianceTextEn: string;
  public issuerConsentTextAi: string;
  public issuerConsentDataSharingText: string;
  public complianceTextDe: string;
  public impressumUrl: string;
  public termsAndConditionsUrl: string;
  public contactUrl: string;
  public privacyPolicyUrl: string;
  public alertUsers: string[];
  public registrationConfig: SelfRegistrationConfig;

  /**
   * Selling-geography intent for marketplace owners. Region codes (e.g. "EU") the
   * partner chose. The effective allowed countries are derived BE-side from
   * regions + includes − excludes.
   */
  public selectedRegions: string[];
  /** Extra ISO-2 country codes added on top of the region expansion. */
  public includedCountries: string[];
  /** ISO-2 country codes explicitly excluded from the expanded region set. */
  public excludedCountries: string[];
  /** Read-only effective ISO-2 country list for eligibility checks. */
  public effectiveAllowedCountries: string[];

  public financialUmbrella: string;
  public commissionBasisText: string;
  public proportionCommissionUpfront: string;
  public minCommissionUpfrontBasisPoints: string;
  public proportionManagementFee: string;
  public minManagementFeeBasisPoints: string;
  public proportionCarriedInterest: string;
  public minCarriedInterestBasisPoints: string;
  public availableLanguages: Language[];
  public versionNumber?: string;
  public hasMarketplaceCapabilities(): boolean {
    return !!this.isMarketplace;
  }

  public hasDueDiligenceCapabilities(): boolean {
    return !!this.isDueDiligence;
  }

  public isMarketplaceOnly(): boolean {
    return this.hasMarketplaceCapabilities() && !this.hasDueDiligenceCapabilities();
  }

  public isDueDiligenceOnly(): boolean {
    return this.hasDueDiligenceCapabilities() && !this.hasMarketplaceCapabilities();
  }

  public supportsBothTypes(): boolean {
    return this.hasMarketplaceCapabilities() && this.hasDueDiligenceCapabilities();
  }

  public hasAnyPartnerType(): boolean {
    return this.hasMarketplaceCapabilities() || this.hasDueDiligenceCapabilities();
  }

  public static create(data: Partial<Partner> = {}): Partner {
    return Object.assign(new Partner(), {
      selectedRegions: [],
      includedCountries: [],
      excludedCountries: [],
      effectiveAllowedCountries: [],
      uid: undefined,
      fullCompanyName: undefined,
      logoImageLight: undefined,
      logoImageDark: undefined,
      faviconImage: undefined,
      loginPageImage: undefined,
      loginPageBackgroundColor: undefined,
      domain: undefined,
      website: undefined,
      isMarketplace: false,
      isDueDiligence: false,
      allowIssuerSelfRegister: undefined,
      allowWmSelfRegister: undefined,
      allowClientSelfRegister: undefined,
      hasOrganizations: false,
      clientManagementCompany: undefined,
      selfRegisterClientType: undefined,
      primaryColor: undefined,
      secondaryColor: undefined,
      sidebarBackgroundColor: undefined,
      sidebarTextColor: undefined,
      buttonRounding: undefined,
      theme: undefined,
      complianceTextEn: undefined,
      issuerConsentTextAi: undefined,
      issuerConsentDataSharingText: undefined,
      complianceTextDe: undefined,
      impressumUrl: undefined,
      termsAndConditionsUrl: undefined,
      contactUrl: undefined,
      privacyPolicyUrl: undefined,
      alertUsers: undefined,
      registrationConfig: undefined,

      financialUmbrella: undefined,
      commissionBasisText: undefined,
      proportionCommissionUpfront: undefined,
      minCommissionUpfrontBasisPoints: undefined,
      proportionManagementFee: undefined,
      minManagementFeeBasisPoints: undefined,
      proportionCarriedInterest: undefined,
      minCarriedInterestBasisPoints: undefined,
      availableLanguages: undefined,
      ...data,
    });
  }

  public static createEmpty(): Partner {
    return Object.assign(new Partner(), {
      selectedRegions: [],
      includedCountries: [],
      excludedCountries: [],
      effectiveAllowedCountries: [],
      id: undefined,
      uid: undefined,
      fullCompanyName: undefined,
      logoImageLight: undefined,
      logoImageDark: undefined,
      faviconImage: undefined,
      loginPageImage: undefined,
      loginPageBackgroundColor: undefined,
      domain: undefined,
      website: undefined,
      isMarketplace: false,
      isDueDiligence: false,

      allowIssuerSelfRegister: undefined,
      allowWmSelfRegister: undefined,
      allowClientSelfRegister: undefined,
      hasOrganizations: false,
      clientManagementCompany: undefined,
      selfRegisterClientType: undefined,

      primaryColor: undefined,
      secondaryColor: undefined,
      sidebarBackgroundColor: undefined,
      sidebarTextColor: undefined,
      buttonRounding: undefined,
      theme: undefined,
      complianceTextEn: undefined,
      issuerConsentTextAi: undefined,
      issuerConsentDataSharingText: undefined,
      complianceTextDe: undefined,

      impressumUrl: undefined,
      termsAndConditionsUrl: undefined,
      contactUrl: undefined,
      privacyPolicyUrl: undefined,
      alertUsers: undefined,
      registrationConfig: undefined,

      financialUmbrella: undefined,
      commissionBasisText: undefined,
      proportionCommissionUpfront: undefined,
      minCommissionUpfrontBasisPoints: undefined,
      proportionManagementFee: undefined,
      minManagementFeeBasisPoints: undefined,
      proportionCarriedInterest: undefined,
      minCarriedInterestBasisPoints: undefined,
      availableLanguages: undefined,
    });
  }
}
